import { Component, computed, effect, inject, input, linkedSignal } from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import { lucideSettings2 } from '@ng-icons/lucide';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmItemSeparator } from '@spartan-ng/helm/item';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';

import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { LivrableCaractériséStore } from '../../../../livrables-caractérisés/store/livrable-caracterisé.store';
import { Livrable } from '../../../../référentiel-métier/référentiel-métier-périmètre/perimetre-metier/models/livrable.model';
import { CheckboxSubmenuItemComponent } from '../../../../shared/components/checkbox-submenu-item/checkbox-submenu-item.component';
import { ExpansionPanel } from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { TrierParPipe } from '../../../../shared/pipes/trier-par.pipe';
import { EtapeStore } from '../../../parcours-etape/store/etape.store';
import { ModuleStore } from '../../../parcours-module/store/module.store';
import { ModuleDetailFormService } from '../module-detail-form.service';
import { FormDetailsLivrablesCaracteristiquesComponent } from './form-details-livrables-caracteristiques/form-details-livrables-caracteristiques.component';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-detail-livrables',
  imports: [
    CheckboxSubmenuItemComponent,
    ExpansionPanel,
    HlmItemSeparator,
    HlmButtonImports,
    HlmIconImports,

    HlmTooltipImports,
    HlmCheckboxImports,
    TrierParPipe,
    ChipComponent,
  ],
  templateUrl: './detail-livrables.component.html',
  providers: [
    provideIcons({
      lucideSettings2,
    }),
  ],
})
export class DetailLivrablesComponent {
  readonly #moduleStore = inject(ModuleStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #etapeStore = inject(EtapeStore);
  readonly #livrableCaractériséStore = inject(LivrableCaractériséStore);
  readonly #detailFormService = inject(ModuleDetailFormService);

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly peutModifier = input.required<boolean>();

  readonly traductions = linkedSignal(() => this.referentiel().metadonnees.traductions);
  moduleSelectionne = computed(() => this.#moduleStore.selectedEntity());
  etapeSelectionnee = computed(() => this.#etapeStore.selectedEntity());

  isLivrableGeneriqueChecked = linkedSignal(() => this.caracteristiquesLivrableGenerique.value?.length > 0);

  constructor() {
    effect(() => {
      const module = this.moduleSelectionne();
      const etape = this.etapeSelectionnee();

      if (module && etape) {
        this.#livrableCaractériséStore.charger(module.id);
        const livrablesCaractérisésModule = module.livrablesCaracterises.flatMap((x) => x.idLivrable);
        const livrables = etape.famillesLivrables
          .flatMap((x) => x.livrables!)
          .filter((x) => livrablesCaractérisésModule.some((id) => id === x!.id));
        this.livrables.setValue(livrables, { emitEvent: false });
      }
    });
  }

  ouvreSousCaracteristiques(livrable?: Livrable) {
    const module = this.moduleSelectionne();
    if (module) {
      const nomLivrable = livrable ? livrable.nom : 'générique';
      this.#sidepanelService.setAdditionalComponentToDisplay(
        'Définir les sous-caractéristiques des livrables',
        FormDetailsLivrablesCaracteristiquesComponent,
        `Choisissez les sous-caractéristiques qui correspondent au ${this.traductions().livrable} <b>${nomLivrable}.</b>`,
        {
          referentiel: this.referentiel(),
          livrableCaracterise: livrable
            ? this.#livrableCaractériséStore.entities().find((x) => x.idLivrable === livrable.id)
            : null,
          idLivrable: livrable?.id,
        },
      );
      this.#sidepanelService.open();
    }
  }

  isSelected(livrable: Livrable) {
    return (
      this.livrables.value.some((x) => x.id === livrable.id) &&
      this.#livrableCaractériséStore.entities().some((x) => x.idLivrable === livrable.id)
    );
  }

  toggleCheckbox() {
    this.isLivrableGeneriqueChecked.set(!this.isLivrableGeneriqueChecked());
    if (!this.isLivrableGeneriqueChecked()) {
      this.caracteristiquesLivrableGenerique.setValue([]);
    }
  }

  countCaracteristique(livrable: Livrable) {
    const livrableCaracterisé = this.#livrableCaractériséStore.entities().find((x) => x.idLivrable === livrable.id);

    if (!livrableCaracterisé) return 0;

    return livrableCaracterisé.caracteristiques.length;
  }

  get livrables() {
    return this.#detailFormService.livrables;
  }

  get livrablesGroup() {
    return this.#detailFormService.livrablesGroup;
  }

  get caracteristiquesLivrableGenerique() {
    return this.#detailFormService.caracteristiques;
  }
}
